import { useAddMessageMutation } from "../../redux/services/hotel/hotelAPI";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';

export default function Contact() {
  const [addMessage, { error, isError }] = useAddMessageMutation();
  const navigate = useNavigate();

  const form = useFormik({
    initialValues: {
      name: "", family: "", mobile_number: "",
      email: "", subject: "", message: ""
    },
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (isError) {
        Swal.fire({ icon: 'error', text: error?.data || 'Unable to send message.', confirmButtonText: 'OK', customClass: { popup: 'sweetalert2-custom-popup' } });
        setSubmitting(false);
        return;
      }

      const response = await addMessage({
        first_name: values.name,
        last_name: values.family,
        email: values.email,
        mobile_number: values.mobile_number,
        subject: values.subject,
        message: values.message,
      });

      if (response?.data) {
        Swal.fire({ icon: 'success', text: response.data.message, timer: 1800, showConfirmButton: false, customClass: { popup: 'sweetalert2-custom-popup' } });
        resetForm();
        navigate('/');
      } else {
        Swal.fire({ icon: 'warning', text: response?.error?.data || 'Unable to send message.', confirmButtonText: 'OK', customClass: { popup: 'sweetalert2-custom-popup' } });
      }

      setTimeout(() => setSubmitting(false), 300);
    },
    validate: (values) => {
      const errors = {};
      if (!values.name) errors.name = 'Name is required';
      if (!values.family) errors.family = 'Family name is required';
      if (!values.mobile_number) errors.mobile_number = 'Mobile number is required';
      if (!values.email) errors.email = 'Email is required';
      if (!values.subject) errors.subject = 'Subject is required';
      if (!values.message) errors.message = 'Message is required';
      return errors;
    }
  });

  const fieldClass = (name) => `field ${form.errors[name] && form.touched[name] ? 'border-red-300 ring-4 ring-red-50' : ''}`;

  return (
    <section className="bg-surface py-12 sm:py-16">
      <div className="section-shell grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
        <div className="premium-card bg-slate-950 p-8 text-white">
          <span className="inline-flex rounded-full bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-[0.2em]">Support</span>
          <h1 className="mt-6 text-4xl font-black tracking-tight sm:text-5xl">We are here to help you book with confidence.</h1>
          <p className="mt-5 text-lg leading-8 text-slate-300">Questions about rooms, restaurant reservations, payments, or account details? Send us a message and our team will follow up quickly.</p>

          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            <div className="rounded-3xl bg-white/10 p-5">
              <p className="text-2xl font-black">24/7</p>
              <p className="mt-2 text-sm text-slate-300">Support window</p>
            </div>
            <div className="rounded-3xl bg-white/10 p-5">
              <p className="text-2xl font-black">Fast</p>
              <p className="mt-2 text-sm text-slate-300">Response time</p>
            </div>
            <div className="rounded-3xl bg-white/10 p-5 sm:col-span-1">
              <p className="text-2xl font-black">Trusted</p>
              <p className="mt-2 text-sm text-slate-300">Booking guidance</p>
            </div>
          </div>
        </div>

        <form onSubmit={form.handleSubmit} className="premium-card bg-white p-6 sm:p-8">
          <h2 className="text-2xl font-black tracking-tight text-ink">Send a message</h2>
          <p className="mt-3 leading-7 text-muted">Tell us what you need and we will route your request to the right support channel.</p>

          <div className="mt-7 grid gap-4 sm:grid-cols-2">
            <label>
              <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Name</span>
              <input className={fieldClass('name')} name="name" value={form.values.name} onChange={form.handleChange} onBlur={form.handleBlur} />
              {form.errors.name && form.touched.name && <p className="mt-2 text-sm font-bold text-red-500">{form.errors.name}</p>}
            </label>
            <label>
              <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Family</span>
              <input className={fieldClass('family')} name="family" value={form.values.family} onChange={form.handleChange} onBlur={form.handleBlur} />
              {form.errors.family && form.touched.family && <p className="mt-2 text-sm font-bold text-red-500">{form.errors.family}</p>}
            </label>
            <label>
              <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Mobile</span>
              <input className={fieldClass('mobile_number')} name="mobile_number" value={form.values.mobile_number} onChange={form.handleChange} onBlur={form.handleBlur} />
              {form.errors.mobile_number && form.touched.mobile_number && <p className="mt-2 text-sm font-bold text-red-500">{form.errors.mobile_number}</p>}
            </label>
            <label>
              <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Email</span>
              <input className={fieldClass('email')} name="email" type="email" value={form.values.email} onChange={form.handleChange} onBlur={form.handleBlur} />
              {form.errors.email && form.touched.email && <p className="mt-2 text-sm font-bold text-red-500">{form.errors.email}</p>}
            </label>
            <label className="sm:col-span-2">
              <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Subject</span>
              <input className={fieldClass('subject')} name="subject" value={form.values.subject} onChange={form.handleChange} onBlur={form.handleBlur} />
              {form.errors.subject && form.touched.subject && <p className="mt-2 text-sm font-bold text-red-500">{form.errors.subject}</p>}
            </label>
            <label className="sm:col-span-2">
              <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Message</span>
              <textarea className={fieldClass('message')} name="message" rows="5" value={form.values.message} onChange={form.handleChange} onBlur={form.handleBlur} />
              {form.errors.message && form.touched.message && <p className="mt-2 text-sm font-bold text-red-500">{form.errors.message}</p>}
            </label>
          </div>

          <button type="submit" disabled={form.isSubmitting} className="btn-primary mt-6 w-full">
            Send message
          </button>
        </form>
      </div>
    </section>
  );
}
